/**
 * Optimistic "how many people" hint, persisted in localStorage.
 *
 * Same rationale as hasBoardsHint.ts (the friends list re-hydrates from
 * scratch on every mount), but for the tile COUNT rather than a boolean:
 * BrowseMyFriendsSection's loading skeleton used a fixed 4-tile grid
 * regardless of the real count, so a returning visitor with, say, 10
 * recipients saw a too-short page reflow taller once the real grid
 * hydrated (THE-1140) — short-circuiting any scroll offset a Back
 * navigation tried to restore onto it. Remembering the last hydrated
 * count lets the skeleton reserve close to the real height up front.
 *
 * Stored alongside the uid it was hydrated for (QA on THE-1140 PR #868):
 * this key is one shared localStorage slot, not account-scoped, and
 * sign-out never clears it — so without the uid check, a device that's
 * seen a big account would show every OTHER signed-in account (a shared
 * computer, or the same device after switching accounts) a skeleton
 * sized for someone else's count on its first load. A mismatched uid is
 * just ignored, same effective behavior as no hint at all.
 */

const KEY = 'thea_people_count';

function readStored(): { uid: string | null; count: number } | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw == null) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      (typeof parsed.uid === 'string' || parsed.uid === null) &&
      Number.isFinite(parsed.count)
    ) {
      return { uid: parsed.uid, count: parsed.count };
    }
    return null;
  } catch {
    return null;
  }
}

export function readPeopleCountHint(uid: string | null): number {
  const stored = readStored();
  if (!stored || stored.uid !== uid) return 0;
  return stored.count > 0 ? Math.floor(stored.count) : 0;
}

export function writePeopleCountHint(uid: string | null, count: number): void {
  try {
    if (count > 0) {
      window.localStorage.setItem(KEY, JSON.stringify({ uid, count }));
    } else {
      window.localStorage.removeItem(KEY);
    }
  } catch {
    /* localStorage unavailable (private mode) — hint stays best-effort */
  }
}
